import _ from 'lodash';
import moment from 'moment';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import {
  ClientLink,
  DateTime,
  ExportDropdown,
  Hours,
  InternalClientTooltip,
  PeriodFilter,
  RouteLink,
  SingleSelectFilter,
  Tooltip,
  Widget,
  YesNoFilter,
} from '~/components';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  FilterButton,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
  ProjectRoleFiltersBar,
  ProjectRoleFiltersGroup,
  TaskFiltersBar,
  TaskFiltersGroup,
  TimeFiltersBar,
  TimeFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useClientFilters,
  useDateTimeFormat,
  useDocumentTitle,
  useHoursFormat,
  useMemberFilters,
  useProjectFilters,
  useProjectRoleFilters,
  useSearchParams,
  useSearchParamsConfig,
  useTaskFilters,
  useTimeEntriesReportURL,
  useTimeFilters,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';
import { intervals, intervalsByScope } from './intervals';

function TimeSummaryByClientByTimeUnit({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const roleFilters = useProjectRoleFilters();
  const taskFilters = useTaskFilters();
  const memberFilters = useMemberFilters();
  const timeFilters = useTimeFilters();

  const [params, setParams] = useState({
    period: null,
    unit: '',
    includeClientsWithoutTime: false,
    ...memberFilters.filters,
    ...clientFilters.filters,
    ...projectFilters.filters,
    ...roleFilters.filters,
    ...taskFilters.filters,
    ...timeFilters.filters,
  });

  const searchParamsConfig = useSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.past_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.past_30_days,
            week: intervals.past_12_weeks,
            month: intervals.past_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },

        // Time Filters
        includeClientsWithoutTime: {
          default: false,
          serialize: (value) => (value === true ? 'true' : 'false'),
          deserialize: (value) => value === 'true',
        },

        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...roleFilters.searchParamsConfig,
        ...taskFilters.searchParamsConfig,
        ...timeFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters, memberFilters, taskFilters, roleFilters, timeFilters],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      includeClientsWithoutTime: params.includeClientsWithoutTime ? 'true' : undefined,

      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...roleFilters.mapUrlSearchParams(params),
      ...taskFilters.mapUrlSearchParams(params),
      ...timeFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters, memberFilters, roleFilters, taskFilters, timeFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.past_30_days,
                week: intervals.past_12_weeks,
                month: intervals.past_6_months,
              }[value],
            })
          }
        />

        <YesNoFilter
          icon="filter"
          placeholder="Include clients without time"
          value={params.includeClientsWithoutTime ? 'yes' : 'no'}
          onChange={({ target: { value } }) => handleApplyFilters({ includeClientsWithoutTime: value === 'yes' })}
        />

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />
        <ProjectRoleFiltersBar filters={params} onChange={handleApplyFilters} />
        <TaskFiltersBar filters={params} onChange={handleApplyFilters} />
        <MemberFiltersBar filters={params} onChange={handleApplyFilters} />
        <TimeFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { clients, records, periods, start, end } = useMemo(() => {
    const clients = _.orderBy(report.clients, (p) => p.name.toLowerCase());

    const records = report.records.reduce(
      (a, v) => {
        a.total.billable += v.billable || 0;
        a.total.nonBillable += v.nonBillable || 0;
        a.total.internal += v.internal || 0;
        a.total.total += v.total || 0;

        // Clients
        a.clients[v.clientId] = a.clients[v.clientId] || {
          total: {
            billable: 0,
            nonBillable: 0,
            internal: 0,
            total: 0,
          },
          periods: {},
        };
        a.clients[v.clientId].total.billable += v.billable || 0;
        a.clients[v.clientId].total.nonBillable += v.nonBillable || 0;
        a.clients[v.clientId].total.internal += v.internal || 0;
        a.clients[v.clientId].total.total += v.total || 0;

        a.clients[v.clientId].periods[v.start] = v;

        // Periods
        if (!a.periods[v.start])
          a.periods[v.start] = {
            start: v.start,
            end: v.end,
            billable: 0,
            nonBillable: 0,
            internal: 0,
            total: 0,
          };
        a.periods[v.start].billable += v.billable || 0;
        a.periods[v.start].nonBillable += v.nonBillable || 0;
        a.periods[v.start].internal += v.internal || 0;
        a.periods[v.start].total += v.total || 0;

        return a;
      },
      {
        clients: {},
        periods: {},
        total: {
          billable: 0,
          nonBillable: 0,
          internal: 0,
          total: 0,
        },
      },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      const periodStart = moment(start).add(index, params.unit).format(dateFormats.isoDate);
      const periodEnd = moment(periodStart)
        .endOf({ day: 'day', week: 'isoWeek', month: 'month' }[params.unit])
        .format(dateFormats.isoDate);
      periods.push({ start: periodStart, end: periodEnd });
    }

    return { clients, records, periods, start, end };
  }, [report, params.period.start, params.period.end, params.unit]);

  const timeDetail = useTimeEntriesReportURL({
    params,
    query: {
      start,
      end,
      timeBillableTypeId: ['billable', 'non_billable', 'internal'],
    },
  });

  const history = useHistory();

  const handleChartClick = ({ start, end, timeBillableTypeId }) => {
    history.push(timeDetail({ start, end, timeBillableTypeId }));
  };

  return (
    <>
      <Report.Summary>
        <Chart periods={periods} records={records} unit={params.unit} onClick={handleChartClick} />
      </Report.Summary>

      <Report.Table>
        <Table>
          <Table.Header>
            <Table.Column sticky minWidth="16rem">
              Client
            </Table.Column>

            {periods.map(({ start }) => (
              <Table.Column key={start} align="right" width="7rem">
                {
                  {
                    day: <DateTime value={start} />,
                    week: <DateTime value={start} />,
                    month: moment(start).format(dateFormats.monthYear),
                  }[params.unit]
                }
              </Table.Column>
            ))}

            <Table.Column align="right" width="7rem">
              Total
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {clients.map((client) => {
              return (
                <Table.Row key={client.id}>
                  <Table.Cell>
                    <ClientLink client={client} />
                    {client.isInternal && <InternalClientTooltip />}
                  </Table.Cell>

                  {periods.map((period) => {
                    const value = records.clients[client.id]?.periods[period.start];

                    return (
                      <Table.Cell key={period.start}>
                        <CellValue
                          value={value}
                          to={timeDetail({
                            clientId: client.id,
                            start: period.start,
                            end: period.end,
                          })}
                        />
                      </Table.Cell>
                    );
                  })}

                  <Table.Cell>
                    <CellValue value={records.clients[client.id]?.total} to={timeDetail({ clientId: client.id })} />
                  </Table.Cell>
                </Table.Row>
              );
            })}

            <Table.Row style={{ fontWeight: weights.bold }}>
              <Table.Cell>Total</Table.Cell>

              {periods.map((period) => {
                const value = records.periods[period.start];

                return (
                  <Table.Cell key={period.start}>
                    <CellValue value={value} to={timeDetail({ start: period.start, end: period.end })} />
                  </Table.Cell>
                );
              })}

              <Table.Cell>
                <CellValue value={records.total} to={timeDetail()} />
              </Table.Cell>
            </Table.Row>
          </Table.Body>

          <Table.Status total={clients.length} label="Client" isLoading={query.status !== 'ready'} />
        </Table>
      </Report.Table>
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <YesNoFilter
        icon="filter"
        placeholder="Include clients without time"
        value={filters.includeClientsWithoutTime ? 'yes' : 'no'}
        onChange={({ target: { value } }) => handleFilter({ includeClientsWithoutTime: value === 'yes' })}
      />

      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.past_30_days,
              week: intervals.past_12_weeks,
              month: intervals.past_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectRoleFiltersGroup filters={filters} onChange={handleFilter} />

      <TaskFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <TimeFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

const TooltipContainer = styled.div`
  min-width: 14rem;
  padding: 0.75rem 0;
`;

const TooltipInfo = styled.div`
  display: flex;
  font-size: 0.75rem;

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const TooltipLabel = styled.div`
  width: 15rem;
  padding-right: 0.5rem;
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  flex-shrink: 0;
  white-space: wrap;
`;

const TooltipValue = styled.div`
  white-space: wrap;
`;

function TooltipHours({ value }) {
  return (
    <>
      <Hours value={value} /> {_.isNumber(value) && pluralize('hours', value)}
    </>
  );
}

function CellValue({ value, to }) {
  if (!value)
    return to ? (
      <RouteLink to={to}>
        <Hours value={null} />
      </RouteLink>
    ) : (
      <Hours value={null} />
    );

  return (
    <Tooltip
      maxWidth="22rem"
      message={
        <TooltipContainer>
          <TooltipInfo>
            <TooltipLabel>Client Billable</TooltipLabel>
            <TooltipValue>
              <TooltipHours value={value.billable} />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Client Non-Billable</TooltipLabel>
            <TooltipHours value={value.nonBillable} />
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Internal</TooltipLabel>
            <TooltipHours value={value.internal} />
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Total</TooltipLabel>
            <TooltipHours value={value.total} />
          </TooltipInfo>
        </TooltipContainer>
      }>
      {to ? (
        <RouteLink to={to}>
          <Hours value={value.total} />
        </RouteLink>
      ) : (
        <Hours value={value.total} />
      )}
    </Tooltip>
  );
}

const getMax = (periods, defaultMax = 8, precision = 2) => {
  let max = Math.max(...periods.map(({ total }) => Math.round(total) || 0), defaultMax) * 1.1;
  max = Math.floor(max / precision) * precision + precision;

  return max;
};

function Chart({ periods, records, unit, onClick }) {
  const dateTimeFormat = useDateTimeFormat(dateFormats.compactDate);

  const { settings, datasets } = useMemo(() => {
    const data = periods.map((period) => records.periods[period.start] ?? period);

    const settings = {
      day: () => ({
        labels: data.map(({ start }) => dateTimeFormat.format(start)),
        max: getMax(data, 8, 2),
        stepSize: 2,
      }),
      week: () => ({
        labels: data.map(({ start }) => dateTimeFormat.format(start)),
        max: getMax(data, 8, 2),
        stepSize: 10,
      }),
      month: () => ({
        labels: data.map(({ start }) => moment(start).format(dateFormats.monthYear)),
        max: getMax(data, 8, 2),
        stepSize: 20,
      }),
    }[unit]();

    const datasets = [
      {
        id: 'billable',
        label: 'Client Billable    ',
        data: data.map(({ billable }) => billable),
        backgroundColor: colors.success,
      },
      {
        id: 'non-billable',
        label: 'Client Non-Billable    ',
        data: data.map(({ nonBillable }) => nonBillable),
        backgroundColor: colors.danger,
      },
      {
        id: 'internal',
        label: 'Internal    ',
        data: data.map(({ internal }) => internal),
        backgroundColor: colors.warning,
      },
      {
        id: 'total',
        label: 'Total',
        data: data.map(({ total }) => (total ? 0 : 0.25)),
        backgroundColor: colors.grey5,
      },
    ];

    return { settings, datasets };
  }, [periods, records, unit, dateTimeFormat]);

  const hoursFormat = {
    tooltip: useHoursFormat({ minimumFractionDigits: 0, maximumFractionDigits: 2 }),
  };

  const handleClick = (_event, [element]) => {
    if (!element) return;

    const billableTypes = ['billable', 'non_billable', 'internal'];

    const timeBillableTypeId = billableTypes[element.datasetIndex];
    const { start, end } = periods[element.index];

    onClick({ start, end, timeBillableTypeId });
  };

  return (
    <Widget style={{ height: '20rem' }}>
      <Bar
        data={{
          labels: settings.labels,
          datasets: datasets,
        }}
        options={{
          maintainAspectRatio: false,

          layout: {
            padding: { top: 12 },
          },

          plugins: {
            legend: {
              onClick: null,
              labels: {
                font: { size: 12 },
                pointStyleWidth: 14,
                boxHeight: 10,
                filter: (item) => !item.text.includes('Total'),
                usePointStyle: true,
              },
            },

            tooltip: {
              filter: (item) => item.datasetIndex <= 3,
              callbacks: {
                title: () => '',
                label: (tooltip) => {
                  let label = (tooltip.dataset.label || '').trim();
                  if (label) {
                    label += ': ';
                  }
                  label += hoursFormat.tooltip.format(tooltip.parsed.y);
                  return label;
                },
              },
            },
          },

          scales: {
            x: {
              stacked: true,
              grid: { display: false },
              ticks: {
                font: {
                  size: 12,
                  weight: 'bold',
                },
                color: colors.grey40,
                minRotation: settings.rotation,
                maxRotation: settings.rotation,
              },
            },

            y: {
              display: true,
              stacked: true,
              grid: { display: true, color: colors.grey10 },
              border: { display: false },
              max: settings.max,
              ticks: { stepSize: settings.stepSize, color: colors.grey40 },
            },
          },

          onHover: (e, chartElement) => {
            e.native.target.style.cursor = chartElement.length ? 'pointer' : 'default';
          },

          onClick: handleClick,
        }}
      />
    </Widget>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}

export default TimeSummaryByClientByTimeUnit;
